import { useEffect, useState, useRef, useLayoutEffect } from "react";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";

const SectionMenu = (props) => {
  const {
    from,
    linedata,
    removeLine,
    handleOpen,
    handleClose,
    DeleteLines,
    handleOpenCI,
    isInspection,
    RenameExisting,
    AllCategoryList,
    assignFlagInLine,
    OpenEditableMenu,
    isInspectionLines,
    colorIndicatorList,
    duplicateCateOrLine,
    assignFlagInCategory,
    handlePrintInReport,
    setPrintableForCategory,
    printableForCategory,
  } = props;
  const [colorMenu, setColorMenu] = useState(false);
  const toggleColorMenu = (open) => setColorMenu(open);
  const menuRef = useRef(null);
  const [placement, setPlacement] = useState("down");
  const [colorPlacement, setColorPlacement] = useState("down");

  const listRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartScrollTopRef = useRef(0);
  const [scrollProgress, setScrollProgress] = useState({ thumbTop: 0, thumbHeight: 40, hasOverflow: false });

  const handleListScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight <= clientHeight) {
      if (scrollProgress.hasOverflow) {
        setScrollProgress((prev) => ({ ...prev, hasOverflow: false }));
      }
      return;
    }
    const trackHeight = clientHeight;
    const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = trackHeight - thumbHeight;
    const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbTop : 0;

    setScrollProgress({
      thumbTop,
      thumbHeight,
      hasOverflow: true,
    });
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    dragStartYRef.current = e.clientY;
    if (listRef.current) {
      dragStartScrollTopRef.current = listRef.current.scrollTop;
    }
    document.body.style.cursor = "pointer";

    const handleMouseMove = (moveEvent) => {
      if (!isDraggingRef.current || !listRef.current) return;
      const { scrollHeight, clientHeight } = listRef.current;
      const trackHeight = clientHeight;
      const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
      const maxScrollTop = scrollHeight - clientHeight;
      const maxThumbTop = trackHeight - thumbHeight;

      if (maxThumbTop <= 0) return;

      const deltaY = moveEvent.clientY - dragStartYRef.current;
      const scrollDelta = (deltaY / maxThumbTop) * maxScrollTop;
      listRef.current.scrollTop = dragStartScrollTopRef.current + scrollDelta;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleTrackClick = (e) => {
    e.stopPropagation();
    if (!listRef.current) return;
    const trackEl = e.currentTarget;
    const rect = trackEl.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const { scrollHeight, clientHeight } = listRef.current;
    const trackHeight = clientHeight;
    const maxScrollTop = scrollHeight - clientHeight;

    const targetScrollTop = (clickY / trackHeight) * maxScrollTop;
    listRef.current.scrollTop = targetScrollTop;
  };

  useEffect(() => {
    if (colorMenu) {
      const timer = setTimeout(() => {
        handleListScroll();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [colorMenu, colorIndicatorList]);

  useEffect(() => {
    if (typeof setPrintableForCategory === "function") {
      setPrintableForCategory(AllCategoryList?.template?.categories?.find((category) => category?.id == from?.split("_")[1]));
    }
  }, [from]);

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!menuRef.current) return;
      const menuEl = menuRef.current;
      const parentEl = menuEl.parentElement;
      const anchorRect = parentEl ? parentEl.getBoundingClientRect() : menuEl.getBoundingClientRect();
      const menuHeight = menuEl.offsetHeight || 260;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const spaceBelowAnchor = viewportHeight - anchorRect.bottom;
      const spaceAboveAnchor = anchorRect.top;

      if (spaceBelowAnchor < menuHeight && spaceAboveAnchor > spaceBelowAnchor) {
        setPlacement("up");
      } else {
        setPlacement("down");
      }

      const menuRect = menuEl.getBoundingClientRect();
      const colorMenuHeight = 260;
      const assignColorTop = menuRect.top + 90;
      const spaceBelowAssignColor = viewportHeight - assignColorTop;

      if (spaceBelowAssignColor < colorMenuHeight || (viewportHeight - menuRect.bottom) < 50) {
        setColorPlacement("up");
      } else {
        setColorPlacement("down");
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [from, colorMenu]);

  let MenuItems = [
    {
      title: "Edit",
      closeMenu: true,
      icon: <EditIcon />,
      clickEvent: (e) => OpenEditableMenu(e),
    },
    {
      title: "Rename",
      closeMenu: true,
      icon: <TextFieldsIcon />,
      clickEvent: (e) => RenameExisting(e),
    },
    {
      title: "Assign Color",
      closeMenu: false,
      icon: <ColorLensIcon />,
      clickEvent: (e) => toggleColorMenu(e),
    },
    {
      title: "Duplicate",
      closeMenu: true,
      icon: <ContentCopyIcon />,
      clickEvent: (e) => duplicateCateOrLine(e),
    },
    {
      title: "Remove Form Element",
      closeMenu: true,
      icon: <RemoveCircleOutlineRoundedIcon />,
      clickEvent: (e) => removeLine(e),
    },
    {
      title: "Print in Report",
      closeMenu: true,
      icon: <PrintIcon />,
      clickEvent: (e) => handlePrintInReport(e),
      switch: (
        linedata?.is_printable === 0 ? (
          <ToggleOffIcon
            id="default_template_off"
            style={{
              fontSize: "46px",
              color: "#BEBEBE",
              cursor: "pointer",
              marginRight: "7px",
              position: "absolute",
              right: "0px",
              bottom: "60px",
            }}
             onClick={(e) => {
              e.stopPropagation();
              handlePrintInReport(e);
              handleClickAway(e)
            }}
          />
        ) : (
          <ToggleOnIcon
            id="default_template_on"
            style={{
              fontSize: "46px",
              cursor: "pointer",
              color: "#239D80",
              marginRight: "7px",
              position: "absolute",
              right: "0px",
              bottom: "60px",
            }}
             onClick={(e) => {
              e.stopPropagation();
              handlePrintInReport(e);
              handleClickAway(e)
            }}
          />
        )
      ),
    },
    {
      title: "Delete",
      closeMenu: true,
      icon: <DeleteIcon />,
      clickEvent: (e) => DeleteLines(e),
    },
  ];

  if (isInspection && linedata?.hasOwnProperty("type") && linedata["type"] === "CheckBox") {
    console.log("The key-value pair exists.");
  } else {
    console.log("The key-value pair does not exist.");
  }

  isInspection === 1 && from?.split("_")[0] !== "SUBCATEGORY" && MenuItems.splice(2, 2);
  isInspection === 1 && from?.split("_")[0] !== "SUBCATEGORY" && MenuItems.splice(0, 1);
  isInspectionLines && MenuItems.splice(0, 1);
  from?.split("_")[0] === "additional" && MenuItems.splice(0, 1) && MenuItems.splice(1, 2) && MenuItems.splice(2, 1);
  from?.split("_")[0] === "comments" && MenuItems.splice(0, 1) && MenuItems.splice(1, 3);
  AllCategoryList?.template?.is_texas && (MenuItems = MenuItems.filter(item => item.title !== "Print in Report"));

  const handleClickAway = (e) => {
    handleClose && handleClose(e);
    toggleColorMenu(false);
  };

  function truncateString(str) {
    if (!str) return "";
    if (str.length > 20) {
      return str.slice(0, 15) + "...";
    }
    return str;
  }

  return (
    <ClickAwayListener onClickAway={(e) => handleClickAway(e)}>
      <Grid
        onClick={(e) => e.stopPropagation(e)}
        ref={menuRef}
        className={`sectionMenu ${placement === "up" ? "sectionMenu--up" : ""}`}
        style={placement === "up" ? { top: "auto", bottom: "0px" } : { top: "0px", bottom: "auto" }}
      >
        <CloseIcon
          role="button"
          tabIndex={0}
          onClick={(e) => handleClickAway(e)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClickAway(e);
            }
          }}
          className="sectionMenu__close"
          style={{ cursor: "pointer" }}
        />
        <div className="sectionMenu__items">
          {MenuItems.map((items, index) =>
            (items.title === "Remove Form Element" && from?.split("_")[0] !== "additional") ||
              (items.title === "Print in Report" && from?.split("_")[0] === "comments") || ((isInspection === 1 || isInspection) && items.title === "Print in Report") ? null : (
              <div
                role="button"
                tabIndex={0}
                style={{ paddingBottom: "16px", cursor: "pointer" }}
                key={index}
                onClick={(e) => {
                  if (items?.title === "Assign Color") {
                    toggleColorMenu((prev) => !prev);
                    return;
                  }
                  items.clickEvent && items.clickEvent(e);
                  items.closeMenu && handleOpen && handleOpen(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (items?.title === "Assign Color") {
                      toggleColorMenu((prev) => !prev);
                      return;
                    }
                    items.clickEvent && items.clickEvent(e);
                    items.closeMenu && handleOpen && handleOpen(e);
                  }
                }}
                onMouseEnter={(e) => {
                  items?.title === "Assign Color" ? items.clickEvent(true) : toggleColorMenu(false);
                }}
              >
                {items.icon}
                <span className="sectionMenu__items__text">{items.title}</span>
                {items?.title === "Assign Color" && <ArrowForwardIosIcon className="sectionMenu__items__arrow" />}
                {items?.title === "Print in Report" && items.switch}
              </div>
            )
          )}
        </div>
        {colorMenu && (
          <div
            className={`colorMenu ${colorPlacement === "up" ? "colorMenu--up" : ""}`}
            style={colorPlacement === "up" ? { top: "auto", bottom: "0px" } : { top: "0px", bottom: "auto" }}
          >
            <div style={{ position: "relative" }}>
              <div
                ref={listRef}
                onScroll={handleListScroll}
                className="colorMenu__list"
              >
                {colorIndicatorList?.filter((e) => e?.is_deleted !== 1)?.length > 0 ? (
                  colorIndicatorList
                    ?.filter((e) => e?.is_deleted !== 1)
                    ?.map((val, colorIdx) => {
                      const handleColorSelect = (e) => {
                        if (from?.split("_")[0] === "CATEGORY") {
                          assignFlagInCategory && assignFlagInCategory(val, linedata?.uuid, linedata?.category_id, linedata?.inspector_flagging_guids);
                        } else if (from?.split("_")[0] === "SUBCATEGORY") {
                          assignFlagInLine && assignFlagInLine(val, linedata);
                        }
                        handleClose && handleClose(e);
                      };
                      return (
                        <div
                          key={val?.guid || colorIdx}
                          role="button"
                          tabIndex={0}
                          onClick={handleColorSelect}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleColorSelect(e);
                            }
                          }}
                          style={{
                            display: "flex",
                            gap: "8px",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            border: `${linedata?.inspector_flagging_guids?.includes(val?.guid) ? "1px solid #4282E2" : ""}`,
                            background: `${linedata?.inspector_flagging_guids?.includes(val?.guid) ? "#F0F6FF" : ""}`,
                            marginBottom: "12px",
                            cursor: "pointer",
                          }}
                        >
                          <div className="color_circle" style={{ backgroundColor: val.color_code }}></div>
                          <p className="color_title">{truncateString(val?.name)}</p>
                          {linedata?.inspector_flagging_guids?.includes(val?.guid) && <CheckIcon sx={{ color: "#4282E2", marginLeft: "auto" }} />}
                        </div>
                      );
                    })
                ) : (
                  <p className="colorIndicatorsection">No roles assigned</p>
                )}
              </div>

              {scrollProgress.hasOverflow && (
                <div className="colorMenu__scroll_track" onClick={handleTrackClick}>
                  <div
                    className="colorMenu__scroll_thumb"
                    onMouseDown={handleThumbMouseDown}
                    style={{
                      top: `${scrollProgress.thumbTop}px`,
                      height: `${scrollProgress.thumbHeight}px`,
                    }}
                  />
                </div>
              )}
            </div>
            <Divider />
            <div
              className="colorMenu__edit"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                handleOpenCI && handleOpenCI();
                handleOpen && handleOpen(e);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOpenCI && handleOpenCI();
                  handleOpen && handleOpen(e);
                }
              }}
            >
              <EditIcon />
              <span className="sectionMenu__items__text">Edit list</span>
            </div>
          </div>
        )}
      </Grid>
    </ClickAwayListener>
  );
};

export default SectionMenu;
